// lib/deviceId.ts — EV-424. A stable ANONYMOUS device id (localStorage + cookie)
// so a logged-out user's check-ins can be stored and then CLAIMED to their
// account on login. Same idea as the anonymous-orders flow.
//
// AN-1498 (2026-08-03): this id is now ALSO the cross-property visitor id.
// It is published to a `.honestlee.app` cookie so the marketing sites
// (honestlee.app / web.honestlee.app) and the PWA (vn/thai/stg.honestlee.app)
// recognise the SAME person — they all share one registrable domain, so a single
// domain-scoped cookie stitches the family together with no URL params at all.
// For properties on a DIFFERENT registrable domain (hl-website-v2.tech,
// nexus22.tech) the shared tracker decorates links with ?hl_vid= instead.
//
// 🔴 The resolution order below matters: localStorage ALWAYS wins, so a
// returning PWA user is never re-identified. Their anonymous check-ins are keyed
// on this id — silently minting a new one would orphan their history.
const KEY = 'hl_device_id';           // localStorage + legacy host-only cookie
const SHARED_COOKIE = 'hl_vid';       // NEW — scoped to .honestlee.app
const ROOT_DOMAIN = 'honestlee.app';

function genId(): string {
  try {
    if (typeof crypto !== 'undefined' && (crypto as any).randomUUID) return (crypto as any).randomUUID();
  } catch {}
  return 'dev-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function readCookie(name: string): string {
  try {
    const m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : '';
  } catch { return ''; }
}

/** Visitor id handed over from another HonestLee property via ?hl_vid=. */
function readUrlHandoff(): string {
  try {
    const v = new URLSearchParams(window.location.search).get('hl_vid') || '';
    return /^[A-Za-z0-9._-]{8,64}$/.test(v) ? v : '';
  } catch { return ''; }
}

export function getDeviceId(): string {
  try {
    // 1. an id this origin already owns — always wins, never re-identify
    let v = localStorage.getItem(KEY) || '';
    // 2. published by another *.honestlee.app property
    if (!v) v = readCookie(SHARED_COOKIE);
    // 3. handed over from a different registrable domain
    if (!v) v = readUrlHandoff();
    // 4. this origin's own legacy host-only cookie (pre-AN-1498 visitors)
    if (!v) v = readCookie(KEY);
    // 5. brand new visitor
    if (!v) v = genId();

    localStorage.setItem(KEY, v);

    // Republish on every call so the id survives a localStorage clear AND so the
    // rest of the family can pick it up. Both cookies kept fresh (1 year):
    //   hl_vid       — domain-wide, the cross-property stitch
    //   hl_device_id — host-only, unchanged legacy behaviour
    const common = '; path=/; max-age=31536000; SameSite=Lax';
    document.cookie = `${SHARED_COOKIE}=${encodeURIComponent(v)}${common}; domain=.${ROOT_DOMAIN}`;
    document.cookie = `${KEY}=${encodeURIComponent(v)}${common}`;
    return v;
  } catch {
    return genId();
  }
}
